// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin — "Command Ops" overview, the screen staff land on after signing in)
// Follows the Command Ops mockup. REAL, from the staff endpoints: headline cards (Daily Catch landings, order fulfilment, what needs
// attention, order value), today's Daily Catch landings, the active orders (with a filter) and the Action-required list (unconfirmed orders,
// complaints, erasure requests). Panels the system can't back yet — cold-chain telemetry chart, command-mode controls, driver KYC, micro-hub
// occupancy, the shift-audit / FSSAI / vessel-intake buttons — are dimmed "Coming soon · sample data" panels (SoonPanel) with neutral labels.
// Figures are computed only from what is loaded (all active orders, the latest 100 delivered) and labelled that way; there is no SLA % because
// no delivery-time target exists. Read-only; works for every staff role. Neighbours: components/admin/AdminParts, utils/adminApi.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CircleCheck, Loader2, TriangleAlert } from "lucide-react";
import { LandingsTable, SoonPanel, StatCard, panel } from "../../components/admin/AdminParts";
import useAuth from "../../hooks/useAuth";
import { ACTIVE, fetchAdminDrop, fetchAdminOrders, fetchClaimQueue, fetchErasureQueue } from "../../utils/adminApi";
import type { DropView } from "../../utils/drop";
import { STATUS_LABEL, formatWhen, type Order } from "../../utils/orders";
import { formatInr } from "../../utils/priceFormat";
import { displayName } from "../../utils/staffAuth";

interface Data {
  active: Order[];
  delivered: Order[];
  claims: number;
  requests: number;
  drop: DropView | null;
}

const sum = (orders: Order[]) => orders.reduce((total, order) => total + order.total, 0);

export default function Dashboard() {
  const auth = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchAdminOrders(ACTIVE), fetchAdminOrders(["delivered"]), fetchClaimQueue(), fetchErasureQueue(), fetchAdminDrop().catch(() => null)]).then(
      ([active, delivered, claims, requests, drop]) => !cancelled && setData({ active, delivered, claims: claims.length, requests: requests.length, drop }),
      () => !cancelled && setFailed(true),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (data?.active ?? []).filter((o) => term === "" || `${o.ref} ${o.delivery.name} ${o.delivery.addressLine} ${o.delivery.city}`.toLowerCase().includes(term));
  }, [data, filter]);

  if (failed) return <p role="alert" className="rounded-xl bg-coral-500/10 p-4 font-semibold">We couldn&apos;t load the dashboard. Reload the page to try again.</p>;
  if (!data) return <p role="status" className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>;

  const count = (status: Order["status"]) => data.active.filter((o) => o.status === status).length;
  const staging = count("placed") + count("confirmed") + count("preparing");
  const transit = count("out_for_delivery");
  const all = staging + transit + data.delivered.length;
  const pct = (n: number) => (all === 0 ? 0 : Math.round((n / all) * 100));
  const landings = data.drop?.landings ?? [];
  const packsLeft = landings.reduce((n, l) => n + l.packsLeft, 0);
  const packsTotal = landings.reduce((n, l) => n + l.packsTotal, 0);
  const unconfirmed = count("placed");
  const attention = unconfirmed + data.claims + data.requests;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-teal-700"><span aria-hidden="true" className="h-2 w-2 animate-pulse rounded-full bg-teal-600" /> Live · ZenFresh operations</span>
          <h1 className="mt-1 font-display text-3xl font-bold">Welcome back, {displayName(auth.user?.email ?? null)}</h1>
          <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">What needs a person right now, from your live orders, complaints and today&apos;s Daily Catch.</p>
        </div>
        <div className="flex flex-wrap gap-2" aria-label="Coming soon actions">
          {["Export shift audit (CSV)", "Daily FSSAI report", "Instant vessel haul inbound"].map((label) => (
            <button key={label} type="button" disabled title="Coming soon" className="min-h-11 cursor-not-allowed rounded-xl bg-ocean-700/10 px-4 text-sm font-bold opacity-60 dark:bg-white/10">{label} <span className="text-[10px] font-bold uppercase text-coral-600 dark:text-coral-300">soon</span></button>
          ))}
        </div>
      </div>

      <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="At a glance">
        <li><StatCard label="Awaiting confirmation" value={unconfirmed} testId="count-placed" to="/admin/orders" hint="Paid, not yet confirmed" /></li>
        <li><StatCard label="Out for delivery" value={transit} testId="count-transit" to="/admin/orders" /></li>
        <li><StatCard label="Complaints to review" value={data.claims} testId="count-claims" to="/admin/claims" /></li>
        <li><StatCard label="Erasure requests" value={data.requests} testId="count-requests" to="/admin/requests" /></li>
      </ul>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Daily Catch landings" value={landings.length} testId="stat-landings" to="/admin/landings" hint={landings.length ? `${packsLeft} of ${packsTotal} packs left` : "None published today"} />
        <StatCard
          label="Order fulfilment"
          value={`${data.active.length} active`}
          testId="stat-active"
          hint={
            <>
              <div className="flex h-2 overflow-hidden rounded-full bg-ocean-700/10 dark:bg-white/10" role="img" aria-label={`${pct(data.delivered.length)}% delivered, ${pct(transit)}% out for delivery, ${pct(staging)}% being prepared`}>
                <span className="bg-teal-600" style={{ width: `${pct(data.delivered.length)}%` }} />
                <span className="bg-ocean-800" style={{ width: `${pct(transit)}%` }} />
                <span className="bg-ocean-700/30" style={{ width: `${pct(staging)}%` }} />
              </div>
              <p className="mt-1 flex justify-between"><span>{data.delivered.length} delivered</span><span>{transit} in transit</span><span>{staging} staging</span></p>
            </>
          }
        />
        <StatCard label="Needs attention" value={attention} testId="stat-attention" hint={`${unconfirmed} to confirm · ${data.claims} complaints · ${data.requests} requests`} />
        <StatCard label="Order value" value={formatInr(sum(data.active))} testId="stat-value" hint={`in active orders · ${formatInr(sum(data.delivered))} in the latest ${data.delivered.length} delivered`} />
      </div>

      <div className="flex flex-col gap-6 xl:flex-row">
        <div className="flex flex-col gap-6 xl:w-[65%]">
          <section className={panel} aria-labelledby="land-h">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><h2 id="land-h" className="font-display text-lg font-bold">Today&apos;s Daily Catch landings</h2><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{data.drop?.drop ? `${data.drop.drop.label}${data.drop.drop.published ? "" : " · draft (not visible to customers)"}` : "Nothing published"}</p></div>
              <Link to="/admin/landings" className="text-sm font-bold underline">Open</Link>
            </div>
            <div className="mt-3"><LandingsTable landings={landings} /></div>
          </section>

          <section className={panel} aria-labelledby="active-h">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><h2 id="active-h" className="font-display text-lg font-bold">Active orders</h2><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{data.active.length} in progress</p></div>
              <div><label htmlFor="order-filter" className="sr-only">Filter active orders</label><input id="order-filter" type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by order, customer, locality…" className="min-h-11 w-64 max-w-full rounded-xl bg-ocean-700/5 px-3 text-sm focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/5" /></div>
            </div>
            {visible.length === 0 ? <p className="mt-3 text-sm">No active orders{filter ? " match that" : ""}.</p> : (
              <ul className="mt-3 flex flex-col gap-2">
                {visible.slice(0, 8).map((order) => (
                  <li key={order.ref} className="flex flex-col justify-between gap-2 rounded-xl bg-ocean-700/5 p-3 md:flex-row md:items-center dark:bg-white/5" data-testid="dash-order">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-2"><Link to={`/admin/orders/${order.ref}`} className="font-bold underline">#{order.ref}</Link><span className="rounded-full bg-teal-100 px-2 py-0.5 text-[11px] font-bold text-teal-700">{STATUS_LABEL[order.status]}</span></p>
                      <p className="text-sm">{order.delivery.name} → <span className="font-semibold">{order.delivery.addressLine}, {order.delivery.city}</span></p>
                    </div>
                    <div className="text-right text-sm"><p className="font-bold">{formatInr(order.total)}</p><p className="text-xs text-ocean-800/75 dark:text-foam-100/70">{formatWhen(order.createdAt)}</p></div>
                  </li>
                ))}
              </ul>
            )}
            {visible.length > 8 && <Link to="/admin/orders" className="mt-3 inline-flex min-h-11 items-center text-sm font-bold underline">See all {visible.length}</Link>}
          </section>

          <SoonPanel title="Temperature & cold-chain telemetry" subtitle="Sample chart — needs pod/hub temperature sensors">
            <svg viewBox="0 0 600 160" className="h-44 w-full" preserveAspectRatio="none">
              <rect x="0" y="20" width="600" height="110" rx="4" className="fill-teal-600/10" />
              <path d="M 0,95 Q 75,90 150,92 T 300,88 T 450,91 T 600,87" fill="none" className="stroke-ocean-800" strokeWidth="2.5" />
              <path d="M 0,110 Q 75,102 150,85 T 300,78 T 450,84 T 600,80" fill="none" className="stroke-teal-600" strokeWidth="2.5" />
            </svg>
            <p className="mt-2 flex justify-between text-[11px]"><span>Inbound</span><span>Sorting</span><span>Surge</span><span>Peak dispatch</span><span>Now</span></p>
          </SoonPanel>
        </div>

        <div className="flex flex-col gap-6 xl:w-[35%]">
          <section className={panel} aria-labelledby="act-h">
            <div className="flex items-center justify-between"><h2 id="act-h" className="flex items-center gap-2 font-display text-lg font-bold">{attention > 0 ? <TriangleAlert className="h-5 w-5 text-coral-600" aria-hidden="true" /> : <CircleCheck className="h-5 w-5 text-teal-700" aria-hidden="true" />} Action required ({attention})</h2></div>
            {attention === 0 ? <p className="mt-2 text-sm">All clear — nothing is waiting for you.</p> : (
              <ul className="mt-3 flex flex-col gap-2 text-sm">
                {unconfirmed > 0 && <li className="rounded-xl bg-coral-500/10 p-3"><p className="font-bold">{unconfirmed} paid {unconfirmed === 1 ? "order needs" : "orders need"} confirming</p><Link to="/admin/orders" className="font-bold underline">Open orders</Link></li>}
                {data.claims > 0 && <li className="rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5"><p className="font-bold">{data.claims} {data.claims === 1 ? "complaint" : "complaints"} to review</p><Link to="/admin/claims" className="font-bold underline">Review</Link></li>}
                {data.requests > 0 && <li className="rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5"><p className="font-bold">{data.requests} data erasure {data.requests === 1 ? "request" : "requests"}</p><Link to="/admin/requests" className="font-bold underline">Review</Link></li>}
              </ul>
            )}
          </section>

          <SoonPanel title="Command mode & controls">
            <ul className="space-y-2 text-sm">
              {[["Algorithmic batching", "Auto-cluster orders by route"], ["Strict freshness abort", "Auto-refund if the cold chain breaks"]].map(([t, d]) => (
                <li key={t} className="flex items-center justify-between rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5"><span><span className="block font-bold">{t}</span><span className="text-xs">{d}</span></span><span className="h-6 w-11 rounded-full bg-ocean-700/25" /></li>
              ))}
            </ul>
          </SoonPanel>

          <SoonPanel title="Pending driver KYC">
            <ul className="space-y-2 text-sm">{["Applicant 1", "Applicant 2", "Applicant 3"].map((n) => <li key={n} className="flex items-center justify-between rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5"><span><span className="block font-bold">{n}</span><span className="text-xs">Documents to verify</span></span><span className="text-xs font-bold">Verify · Review</span></li>)}</ul>
          </SoonPanel>

          <SoonPanel title="Micro-hub storage occupancy">
            <ul className="space-y-3 text-sm">{[["Hub A", 88], ["Hub B", 64], ["Hub C", 42]].map(([n, v]) => <li key={n}><p className="flex justify-between font-bold"><span>{n}</span><span>{v}%</span></p><div className="mt-1 h-2 rounded-full bg-ocean-700/10"><div className="h-2 rounded-full bg-ocean-800" style={{ width: `${v}%` }} /></div></li>)}</ul>
          </SoonPanel>
        </div>
      </div>
    </div>
  );
}
